import { useEffect, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import {
  LayoutDashboard, Compass, Map, BookOpen, FileText, MessageCircle,
  Shield, KeyRound, Users, PanelLeftClose, PanelLeftOpen,
  GraduationCap, NotebookPen, Activity, Gauge,
} from "lucide-react";
import iconMark from "../assets/brand/klareet-icon-white.png";
import client, { detectOrgSlug, fetchCobrand } from "../lib/api";

function readCollapsed() {
  try { return localStorage.getItem("klareet.sidebar.collapsed") === "1"; }
  catch (_e) { return false; }
}

/**
 * The post-login shell. A collapsible left panel with the Klareet mark up
 * top, the nav, Ask Klair, and (on a college seat) the college mark at the
 * bottom. Collapsed state is remembered per device. Each item routes to a
 * real screen backed by a real API.
 */
export default function SidebarShell({ product, children }) {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSeatAdmin, setIsSeatAdmin] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [cobrand, setCobrand] = useState(null);
  const [collapsed, setCollapsed] = useState(readCollapsed());
  const [pulseDue, setPulseDue] = useState(false);

  useEffect(() => {
    client.get("/consent/status").then((r) => {
      // KLA-056: display_name and is_minor sit at the top level of the
      // status payload, not under person; reading person.* left the
      // whole nav showing "You" for every signed-in student.
      const d = r?.data || {};
      const raw = (d.display_name || d.person?.display_name || "").trim();
      setMe({
        display_name: raw.split(/\s+/)[0] || "You",
        is_minor: !!(d.is_minor ?? d.person?.is_minor),
      });
    }).catch(() => setMe({ display_name: "You", is_minor: false }));
    client.get("/content-admin/summary").then(() => setIsAdmin(true)).catch(() => setIsAdmin(false));
    client.get("/licensing/pools").then(() => setIsSeatAdmin(true)).catch(() => setIsSeatAdmin(false));
    client.get("/klair/quota/config").then(() => setIsPlatformAdmin(true)).catch(() => setIsPlatformAdmin(false));
    const slug = detectOrgSlug();
    if (slug) fetchCobrand(slug).then(setCobrand).catch(() => setCobrand(null));
  }, []);

  useEffect(() => {
    try { localStorage.setItem("klareet.sidebar.collapsed", collapsed ? "1" : "0"); }
    catch (_e) { /* ignore */ }
  }, [collapsed]);

  useEffect(() => {
    client.get(`/engagement/weekly-checkin?product=${product === "grow" ? "grow" : "start"}`)
      .then((r) => setPulseDue(!!r?.data?.due))
      .catch(() => setPulseDue(false));
  }, [product]);

  const base = product === "grow" ? "/grow" : "";
  const items = [
    { to: `${base}/home`,     label: "Dashboard",    icon: <LayoutDashboard size={16} />, tid: "nav-home" },
    { to: `${base}/report`,   label: "My Snapshot",  icon: <Compass size={16} />,  tid: "nav-report" },
    { to: `${base}/roadmap`,  label: "Roadmap",      icon: <Map size={16} />,      tid: "nav-roadmap" },
    { to: `${base}/learning`, label: "Learning",     icon: <BookOpen size={16} />, tid: "nav-learning" },
    { to: `${base}/journal`,  label: "Journal",      icon: <NotebookPen size={16} />, tid: "nav-journal" },
    { to: `${base}/pulse`,    label: "Weekly pulse", icon: <Activity size={16} />, tid: "nav-pulse" },
    { to: `${base}/resume`,   label: "My One-Pager", icon: <FileText size={16} />, tid: "nav-resume" },
  ];
  if (cobrand) {
    items.push({ to: `${base}/college`, label: "Your college", icon: <GraduationCap size={16} />, tid: "nav-college" });
  }
  if (isSeatAdmin) {
    items.push({ to: "/admin/seats", label: "Seat activation", icon: <KeyRound size={16} />, tid: "nav-admin-seats" });
    items.push({ to: "/admin/cohort", label: "Cohort view", icon: <Users size={16} />, tid: "nav-admin-cohort" });
    items.push({ to: "/admin/college", label: "College page", icon: <GraduationCap size={16} />, tid: "nav-admin-college" });
  }
  if (isAdmin) {
    items.push({ to: "/admin/items", label: "Panel console", icon: <Shield size={16} />, tid: "nav-admin-items" });
    items.push({ to: "/admin/books", label: "Books catalogue", icon: <BookOpen size={16} />, tid: "nav-admin-books" });
  }
  if (isPlatformAdmin) {
    items.push({ to: "/admin/klair-quota", label: "Klair quota", icon: <Gauge size={16} />, tid: "nav-admin-klair-quota" });
  }

  const collegeInitial = (cobrand?.display_name || "C").slice(0, 1).toUpperCase();

  return (
    <div
      className="k-shell relative flex"
      style={{ "--klareet-sb-w": collapsed ? "76px" : "246px" }}
    >
      <aside
        className="k-sidebar hidden lg:flex flex-col fixed top-16 left-0 h-[calc(100vh-4rem)] z-20 border-r print:hidden"
        data-collapsed={collapsed}
        style={{
          borderColor: "hsl(var(--klareet-line-soft))",
          background: "hsl(var(--card) / 0.92)",
          backdropFilter: "blur(8px)",
        }}
        data-testid="sidebar-shell"
      >
        {/* Klareet mark + collapse toggle */}
        <div className={`flex items-center gap-2 pt-4 pb-3 ${collapsed ? "px-2 justify-center flex-col" : "px-4"}`}>
          <div className="w-9 h-9 rounded-[11px] flex items-center justify-center shrink-0" style={{ background: "hsl(var(--klareet-ink))" }}>
            <img src={iconMark} alt="Klareet" className="w-5 h-5" />
          </div>
          {!collapsed && (
            <span className="k-label !mb-0 flex-1 truncate" data-testid="sidebar-brand">
              Klareet {product === "grow" ? "Grow" : "Start"}
            </span>
          )}
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="k-btn k-btn-ghost !p-1.5 !px-1.5 shrink-0"
            aria-label={collapsed ? "expand menu" : "collapse menu"}
            title={collapsed ? "expand" : "collapse"}
            data-testid="sidebar-collapse"
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>

        <nav className={`flex flex-col gap-1 ${collapsed ? "px-2" : "px-3"}`} data-testid="sidebar-nav">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) => `k-nav-item relative ${isActive ? "sidebar-active" : "sidebar-idle"}`}
              data-testid={it.tid}
            >
              {it.icon}
              {!collapsed && <span className="k-nav-label">{it.label}</span>}
              {it.tid === "nav-pulse" && pulseDue && (
                <span
                  className={collapsed ? "absolute top-1.5 right-1.5 w-2 h-2 rounded-full" : "ml-auto w-2 h-2 rounded-full"}
                  style={{ background: "hsl(28 74% 52%)" }}
                  title="pulse due this week"
                  data-testid="nav-pulse-dot"
                />
              )}
            </NavLink>
          ))}
        </nav>

        <div className={`mt-3 ${collapsed ? "px-2" : "px-4"}`}>
          <button
            onClick={() => nav(`${base}/ask`)}
            className={`k-btn k-btn-accent w-full !py-2.5 ${collapsed ? "!px-0" : ""}`}
            title={collapsed ? "Ask Klair" : undefined}
            data-testid="ask-klair-cta"
          >
            <MessageCircle size={14} /> {!collapsed && "Ask Klair"}
          </button>
        </div>

        <div className={`mt-auto pb-5 space-y-3 ${collapsed ? "px-2" : "px-4"}`}>
          {cobrand && (
            <Link
              to={`${base}/college`}
              className={`k-lift rounded-xl border ${collapsed ? "p-1.5 flex justify-center" : "p-2.5 flex items-center gap-2.5"}`}
              style={{ borderColor: "hsl(var(--klareet-line-soft))", background: "hsl(var(--klareet-canvas-2))" }}
              data-testid="college-block"
              title={cobrand.display_name}
            >
              {cobrand.logo_url ? (
                <img src={cobrand.logo_url} alt={cobrand.display_name} className="h-7 w-7 object-contain rounded" data-testid="college-logo" />
              ) : (
                <div
                  className="h-7 w-7 rounded flex items-center justify-center text-xs font-semibold shrink-0"
                  style={{ background: "hsl(var(--klareet-teal))", color: "white" }}
                  data-testid="college-logo"
                >
                  {collegeInitial}
                </div>
              )}
              {!collapsed && (
                <div className="min-w-0">
                  <div className="k-quiet !text-[10px] uppercase tracking-wide">your college</div>
                  <div className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }} data-testid="college-name">
                    {cobrand.display_name}
                  </div>
                </div>
              )}
            </Link>
          )}

          <div
            className={`rounded-lg border ${collapsed ? "p-1.5 flex justify-center" : "flex items-center gap-2.5 p-2.5"}`}
            style={{ borderColor: "hsl(var(--klareet-line-soft))", background: "hsl(var(--klareet-canvas-2))" }}
            data-testid="user-chip"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "hsl(var(--klareet-ink))", color: "white", fontWeight: 600, fontSize: 13 }}
              title={me?.display_name || "You"}
            >
              {(me?.display_name || "Y").slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: "hsl(var(--klareet-ink))" }}>
                  {me?.display_name || "You"}
                </div>
                <div className="k-quiet !text-xs truncate">private read</div>
              </div>
            )}
          </div>
        </div>
      </aside>

      <div className="flex-1 k-content-shell min-w-0 relative">
        {/* Mobile top nav strip */}
        <div
          className="lg:hidden sticky top-16 z-20 border-b overflow-x-auto print:hidden"
          style={{ borderColor: "hsl(var(--klareet-line-soft))", background: "hsl(var(--card) / 0.94)", backdropFilter: "blur(8px)" }}
          data-testid="mobile-topnav"
        >
          <div className="flex gap-1 px-4 py-2 whitespace-nowrap">
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                className={({ isActive }) => `px-3 py-1.5 rounded-full text-xs font-medium ${isActive ? "sidebar-active" : "sidebar-idle"}`}
                data-testid={`${it.tid}-mobile`}
              >
                {it.label}
              </NavLink>
            ))}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
